import { endOfMonth, format, parseISO } from "date-fns";
import { X } from "lucide-react";
import { useMemo, useState } from "react";

import ShowInApplicationLink from "@/components/application/aiAnalysis/ShowInApplicationLink";
import BaseTransactionTable from "@/components/transactions/BaseTransactionTable";
import ExpandableRow from "@/components/transactions/ExpandableRow";
import { Button } from "@/components/ui/button";
import { useAnalysisRunQuery } from "@/hooks/queries/useAnalysisRuns";
import { cn } from "@/lib/utils";
import type { AnalysisRunTransaction } from "@/types/analysisRun";

import type { BlockChange } from "../protocol/protocol";
import { CARD_TABLE, cardCells, cardColumns, ChangeStack } from "./cardTable";
import { NONE } from "./transactionChange";
import type { TransactionCard, TransactionRow } from "./wire";

interface Credit {
  key: string;
  transaction: AnalysisRunTransaction;
  category: BlockChange;
  revenue: BlockChange;
}

/** The latest "YYYY-MM" among the run's rows whose short name is `abbrev` ("Sep"). */
function monthOf(transactions: AnalysisRunTransaction[], abbrev: string): string | null {
  const months = new Set(transactions.map((t) => t.date?.slice(0, 7)).filter(Boolean));
  return (
    [...months]
      .filter(
        (month) => /^\d{4}-\d{2}$/.test(month) && format(parseISO(`${month}-01`), "MMM") === abbrev
      )
      .sort()
      .pop() ?? null
  );
}

/**
 * A Recent Revenue month's credits from the card's run, as the AI Analysis tab lists them, with
 * the ticked rows' changes shown on them: what the month's figure is made of. Read from the
 * tab's own run query, so it is cached and refetched with the tab.
 */
export function ReclassifyMonthCredits({
  card,
  month,
  ticked,
  phone,
  onClose,
}: {
  card: Pick<TransactionCard, "applicationId" | "runId">;
  /** The tile's short month name ("Sep"). */
  month: string;
  ticked: TransactionRow[];
  phone: boolean;
  onClose: () => void;
}) {
  const run = useAnalysisRunQuery(card.runId, card.applicationId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const transactions = run.data?.transactions_json;
  const monthKey = useMemo(
    () => (transactions ? monthOf(transactions, month) : null),
    [transactions, month]
  );
  const credits = useMemo<Credit[]>(() => {
    if (!transactions || !monthKey) return [];
    const changes = new Map(ticked.map((row) => [row.transaction.override_key, row.transaction]));
    return transactions
      .filter((t) => t.date?.startsWith(monthKey) && Number(t.amount) >= 0)
      .sort((a, b) => b.date.localeCompare(a.date))
      .map((t) => {
        const change = t.override_key ? changes.get(t.override_key) : undefined;
        return {
          key: t.override_key || `${t.statement_id}-${t.index}`,
          transaction: t,
          category: change?.category ?? { old: t.txn_type, new: t.txn_type },
          revenue: change?.revenue ?? { old: t.rev, new: t.rev },
        };
      });
  }, [transactions, monthKey, ticked]);

  // Every credit says whether it counts as revenue: that is what the month's figure adds up.
  const columns = cardColumns<Credit>(phone, { label: "Category" });
  const renderRow = (credit: Credit) => {
    const { transaction } = credit;
    const cells = cardCells(
      phone,
      transaction,
      <ChangeStack category={credit.category} revenue={credit.revenue} />
    );
    return (
      <ExpandableRow
        key={credit.key}
        isExpanded={expanded === credit.key}
        onToggle={() => setExpanded((current) => (current === credit.key ? null : credit.key))}
        cells={cells}
        detail={{
          type: "fields",
          fields: [
            { label: "Description:", value: transaction.description || NONE },
            { label: "Memo:", value: transaction.memo || NONE },
            ...(transaction.counterparty
              ? [{ label: "Counterparty:", value: transaction.counterparty }]
              : []),
          ],
        }}
        compact
        highlighted={
          credit.category.old !== credit.category.new || credit.revenue.old !== credit.revenue.new
        }
      />
    );
  };

  const range = monthKey
    ? `${format(parseISO(`${monthKey}-01`), "MMM d")}–${format(endOfMonth(parseISO(`${monthKey}-01`)), "d")}`
    : "";
  return (
    <section
      aria-label={`${month} credits`}
      className={cn("flex min-w-0 flex-col gap-2", !phone && "min-h-0 flex-1")}
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">{month} credits</h4>
        {!phone && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {run.isLoading ? (
        <p className="py-6 text-center text-xs text-muted-foreground">Loading…</p>
      ) : (
        // In the popover, capped at the window's room, the rows scroll; title, pager and count stay.
        <div className={cn(CARD_TABLE, !phone && "flex min-h-0 flex-1 flex-col")}>
          <BaseTransactionTable
            dense
            columns={columns}
            items={credits}
            renderRow={renderRow}
            defaultRowsPerPage={10}
            emptyMessage="No credits this month"
            fill={!phone}
          />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {credits.length} {credits.length === 1 ? "credit" : "credits"}
          {range && ` · ${range}`}
        </span>
        {monthKey && (
          <ShowInApplicationLink
            target={{
              applicationId: card.applicationId,
              run: card.runId,
              filter: { kind: "month", month: monthKey },
              highlight: credits
                .filter((c) => c.category.old !== c.category.new || c.revenue.old !== c.revenue.new)
                .flatMap((c) => (c.transaction.override_key ? [c.transaction.override_key] : [])),
            }}
          />
        )}
      </div>
    </section>
  );
}
